import { memo } from 'react'

import { ServerPrivateRegionAgentInstance } from '@/api/type-server'
import { Button, Modal, Trans } from '@/components'
import { InstanceStatus, instanceStatusMap } from '@/models/instance'
import { cn } from '@/utils/style'

interface ActiveInstancesModalProps {
  open: boolean
  instances: ServerPrivateRegionAgentInstance[]
  onClose: () => void
}

function getInstanceStatusStyle(status: number): string {
  return instanceStatusMap[status as InstanceStatus]?.style ?? 'text-secondary'
}

function getInstanceStatusText(status: number): string {
  return instanceStatusMap[status as InstanceStatus]?.label ?? 'Unknown'
}

export const ActiveInstancesModal = memo(function ActiveInstancesModal({
  open,
  instances,
  onClose,
}: ActiveInstancesModalProps) {
  return (
    <Modal open={open} onClose={onClose} className="w-[800px]">
      <Modal.Header>
        <Trans ns="privateRegion" i18nKey="activeInstances.title">
          Unable to Switch to Standby
        </Trans>
      </Modal.Header>
      <Modal.Body>
        <p className="text-secondary t-medium mb-2">
          <Trans ns="privateRegion" i18nKey="activeInstances.description">
            Active instances detected. Please terminate or migrate all running instances before
            switching to Standby.
          </Trans>
        </p>
        <table className="w-full table-fixed">
          <thead>
            <tr className="bg-neutral-50 rounded-lg">
              <th className="text-secondary t-sm px-4 py-3 text-left w-[25%]">
                <Trans ns="privateRegion" i18nKey="activeInstances.columnId">
                  ID
                </Trans>
              </th>
              <th className="text-secondary t-sm px-4 py-3 text-left w-[30%]">
                <Trans ns="privateRegion" i18nKey="activeInstances.columnName">
                  Name
                </Trans>
              </th>
              <th className="text-secondary t-sm px-4 py-3 text-left w-[25%]">
                <Trans ns="privateRegion" i18nKey="activeInstances.columnOwner">
                  Owner
                </Trans>
              </th>
              <th className="text-secondary t-sm px-4 py-3 text-left w-[20%]">
                <Trans ns="privateRegion" i18nKey="activeInstances.columnStatus">
                  Status
                </Trans>
              </th>
            </tr>
          </thead>
          <tbody className="bg-panel-white">
            {instances.map((instance) => (
              <tr key={instance.resID}>
                <td className="t-sm text-primary px-4 py-3">{instance.resID}</td>
                <td className="t-sm text-primary px-4 py-3">{instance.label || '-'}</td>
                <td className="t-sm text-primary px-4 py-3">{instance.owner || '-'}</td>
                <td className={cn('t-sm px-4 py-3', getInstanceStatusStyle(instance.status))}>
                  {getInstanceStatusText(instance.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]">
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button type="secondary" onClick={onClose} className="min-w-[88px]">
          OK
        </Button>
      </Modal.Footer>
    </Modal>
  )
})
